import { getModuleUrl, useTenant } from "@dehub/theme";
import { ActionIcon, Box, Button, Drawer, Image, Loader, Modal, Text } from "@mantine/core";
import { XIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { ReturnsApi } from "../../apis/returns";
import { IAuth } from "../../interfaces/IAuth";
import { currencyFormat } from "../../utils";
import { dateFormat, dateTimeShortFormat } from "../../utils/date";
import { handleError } from "../../utils/error-handler";
import { message } from "../../utils/Message";
import {
  isDecisionStatus,
  nextActionFor,
  orderModuleReturnPath,
  STAGE_COUNT,
  stageBars,
  type BarState,
  type ReturnStage,
  type WaitingOn,
} from "../../utils/return-queue";
import { CANCELLABLE_STATUSES, getReasonLabel } from "../../utils/return-constants";
import { DisputeModal } from "./dispute-modal";
import { RejectModal } from "./reject-modal";
import { ReturnProposalForm } from "./return-proposal-form";
import classes from "./return-decision.module.css";

const STEP_CLASS: Record<BarState, string> = {
  done: classes.stepDone,
  now: classes.stepNow,
  warn: classes.stepWarn,
  wait: classes.stepWait,
  todo: "",
};

const photoUrl = (p: unknown): string | null =>
  typeof p === "string" ? p : p && typeof p === "object" && "url" in p ? String((p as { url: unknown }).url) : null;

const initials = (n?: string | null) => Array.from((n ?? "").trim()).slice(0, 2).join("").toUpperCase() || "-";

type ViewProps = {
  id: string;
  mode: "drawer" | "page";
  onClose?: () => void;
  onChanged?: () => void;
};

export function ReturnDecisionView({ id, mode, onClose, onChanged }: ViewProps) {
  const { t } = useTranslation(["returns", "common"]);
  const tenant = useTenant();
  const { user, accessToken } = useSelector((s: { auth: IAuth }) => s.auth);
  const myBusinessId = user?.currentBusiness?.id;

  const { data, isLoading, error, mutate } = useSWR(`returns.decision.${id}`, () => ReturnsApi.get(id), { revalidateOnFocus: false });
  const [rejectOpen, setRejectOpen] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [zoom, setZoom] = useState<string | null>(null);

  const refresh = async () => {
    await mutate();
    onChanged?.();
  };

  if (isLoading) {
    return <Box className={classes.root} p="xl"><Loader size="sm" /></Box>;
  }
  const rr = data?.returnRequest ?? data;
  if (error || !rr?.id) {
    return <Box className={classes.root} p="xl"><Text c="dimmed">{t(error ? "returns:decision.loadError" : "returns:decision.notFound")}</Text></Box>;
  }

  const items: any[] = data?.items ?? [];
  const timeline: any[] = data?.timeline ?? rr.timeline ?? [];
  const isSeller = !!myBusinessId && rr.sellerBusinessId === myBusinessId;
  const stage = rr.stage as ReturnStage;
  const waitingOn = rr.waitingOn as WaitingOn;
  const stageAlert = !!rr.stageAlert;
  const bars = stageBars(stage, waitingOn, stageAlert);
  const activeIndex = typeof stage === "number" ? stage : -1;
  const partyName = isSeller ? rr.buyerBusinessName : rr.sellerBusinessName;
  const orderCode = rr.orderNumber;
  const days = Number(rr.daysSinceRequest ?? 0);
  const disputed = rr.status === "DISPUTED";
  const canDecide = isSeller && isDecisionStatus(rr.status);
  const action = waitingOn === "ME" ? nextActionFor(rr.status, isSeller) : null;

  const pillClass =
    stage === "DONE" ? classes.pillDone
      : stage === "CLOSED" ? classes.pillClosed
        : stageAlert ? classes.pillWarn
          : waitingOn === "THEM" ? classes.pillWait
            : classes.pillNow;
  const pillText = disputed
    ? t("returns:decision.disputedN", { n: rr.disputeCount || 1 })
    : t(`returns:status.${rr.status}`, { defaultValue: rr.status });

  const photos: string[] = ((rr.evidencePhotos as unknown[]) ?? []).map(photoUrl).filter((u): u is string => !!u);
  const first = items[0];
  const partial = rr.returnType ? rr.returnType !== "FULL" : items.length > 0;

  const orderModuleUrl = `${getModuleUrl("order", tenant?.modulesDomain)}/access?token=${encodeURIComponent(accessToken ?? "")}&redirect=${encodeURIComponent(orderModuleReturnPath(rr.id, rr.status))}`;

  const handleAccept = async () => {
    if (!window.confirm(t("returns:decision.acceptConfirm"))) return;
    try {
      await ReturnsApi.accept(rr.id, { expectedVersion: rr.version });
      message.success(t("returns:decision.acceptSuccess"));
      await refresh();
    } catch (e) {
      handleError(e, t("common:error"));
    }
  };
  const handleCancel = async () => {
    if (!window.confirm(t("returns:cancelConfirm"))) return;
    try {
      await ReturnsApi.cancel(rr.id, { expectedVersion: rr.version });
      message.success(t("returns:cancelSuccess"));
      await refresh();
    } catch (e) {
      handleError(e, t("returns:cancelError"));
    }
  };

  const kv = (label: string, value?: string | null) =>
    value ? (
      <Box className={classes.kv}>
        <Text span className={classes.kvKey}>{label}</Text>
        <Text span className={classes.kvVal}>{value}</Text>
      </Box>
    ) : null;

  const proposalSummary = rr.refundPercent != null || rr.resolutionType ? (
    <Box component="section" className={classes.section}>
      <Text component="h2" className={classes.sectionTitle}>{t(isSeller ? "returns:decision.lastProposal" : "returns:decision.sellerProposal")}</Text>
      <Box>
        {rr.resolutionType && kv(t("returns:decision.resolution"), t(`returns:decision.res.${rr.resolutionType}.t`, { defaultValue: rr.resolutionType }))}
        {rr.refundPercent != null && kv(t("returns:decision.refundPercent"), `${rr.refundPercent}%`)}
        {rr.refundAmount != null && kv(t("returns:decision.refundAmount"), currencyFormat(rr.refundAmount))}
        {rr.deductionAmount != null && rr.deductionAmount > 0 && kv(t("returns:detail.deduction"), currencyFormat(rr.deductionAmount))}
        {kv(t("returns:decision.deductionReason"), rr.deductionReason)}
        {rr.refundMethod && kv(t("returns:decision.refundMethod"), t(`returns:refundMethod.${rr.refundMethod}`, { defaultValue: rr.refundMethod }))}
        {rr.shippingResponsibility && kv(t("returns:decision.goods"), t(`returns:shipping.${rr.shippingResponsibility}`, { defaultValue: rr.shippingResponsibility }))}
        {kv(t("returns:decision.warehouseLabel"), rr.targetWarehouseName)}
        {kv(t("returns:decision.notesLabel"), rr.proposalNotes)}
      </Box>
    </Box>
  ) : null;

  const head = (
    <Box className={classes.head}>
      <Box className={classes.headTop}>
        <Box className={classes.headTitle}>
          <Text span className={classes.number}>{rr.returnNumber}</Text>
          <Text span className={`${classes.pill} ${pillClass}`}>
            <Box component="span" className={classes.pillDot} />
            {pillText}
          </Text>
        </Box>
        {mode === "drawer" && onClose && (
          <ActionIcon variant="subtle" color="gray" size="lg" radius="md" aria-label={t("returns:decision.close")} onClick={onClose}>
            <XIcon size={18} weight="bold" />
          </ActionIcon>
        )}
      </Box>
      <Box className={classes.metaRow}>
        <Text span className={classes.avatar}>{initials(partyName)}</Text>
        <Text span className={classes.partyName}>{partyName ?? "-"}</Text>
        {orderCode && (
          <>
            <Text span className={classes.sep}>|</Text>
            {rr.invoiceId ? (
              <Link className={classes.soLink} to={`/${isSeller ? "sales-invoice" : "buyer-invoice"}/${rr.invoiceId}/details`}>{orderCode}</Link>
            ) : (
              <Text span className={classes.soLink}>{orderCode}</Text>
            )}
          </>
        )}
        <Text span className={classes.sep}>|</Text>
        <Text span>{t("returns:decision.requestedOn", { date: dateFormat(rr.requestedAt), days })}</Text>
      </Box>
      <Box component="ol" aria-label={t("returns:queue.col.progress")} className={classes.steps}>
        {Array.from({ length: STAGE_COUNT }, (_, i) => (
          <Box key={i} component="li" className={classes.step}>
            <Box component="span" className={`${classes.stepBar} ${STEP_CLASS[bars[i]]}`} />
            <Text span className={`${classes.stepLabel} ${i === activeIndex ? classes.stepLabelActive : ""}`}>{t(`returns:queue.stage.s${i}`)}</Text>
          </Box>
        ))}
      </Box>
    </Box>
  );

  const requestCard = (
    <Box component="section" className={classes.section}>
      <Text component="h2" className={classes.sectionTitle}>{t("returns:decision.request")}</Text>
      <Box className={classes.requestCard}>
        <Box className={classes.thumbs}>
          {photos.length === 0 ? (
            <Box className={classes.thumb}>{t("returns:decision.noEvidence")}</Box>
          ) : (
            photos.slice(0, 4).map((u, i) => (
              <Box key={u} component="button" type="button" className={classes.thumb} aria-label={t("returns:decision.evidence", { n: i + 1 })} onClick={() => setZoom(u)}>
                <Image src={u} alt="" w={76} h={76} fit="cover" />
              </Box>
            ))
          )}
        </Box>
        <Box className={classes.requestMain}>
          <Box className={classes.requestTop}>
            <Text span className={classes.reasonText}>{getReasonLabel(rr.returnReason)}</Text>
            <Text span className={classes.requestAmount}>{currencyFormat(Number(rr.totalRequestedAmount || 0))}</Text>
          </Box>
          {first && (
            <Text span className={classes.itemName}>
              {first.productName}
              {items.length > 1 ? `  ${t("returns:decision.moreItems", { count: items.length - 1 })}` : ""}
            </Text>
          )}
          {first && (
            <Text span className={classes.itemSub}>
              {first.sku ? <><Text span className={classes.mono}>{first.sku}</Text>{" · "}</> : null}
              {t("returns:decision.qtyLine", { qty: first.quantity, price: currencyFormat(Number(first.unitPrice || 0)).replace(/\s*₮$/, "") })}
              {" · "}
              {t(partial ? "returns:decision.partial" : "returns:decision.full")}
            </Text>
          )}
          {rr.reasonDetails && <Text span className={classes.itemSub}>{rr.reasonDetails}</Text>}
        </Box>
      </Box>
    </Box>
  );

  const disputeBlock = disputed && rr.disputeNotes ? (
    <Box component="section" className={classes.section}>
      <Text component="h2" className={classes.sectionTitle}>{t("returns:decision.dispute")}</Text>
      <Box component="blockquote" className={classes.quote}>
        {`\u00ab${rr.disputeNotes}\u00bb`}
        <Text span className={classes.quoteMeta}>
          {t(isSeller ? "returns:decision.disputeMeta" : "returns:decision.disputeMetaBuyer", { date: dateTimeShortFormat(rr.disputedAt) })}
        </Text>
      </Box>
    </Box>
  ) : null;

  const historyBlock = timeline.length > 0 ? (
    <Box component="section" className={classes.section}>
      <Text component="h2" className={classes.sectionTitle}>{t("returns:decision.history")}</Text>
      <Box>
        {timeline.map((e, i) => (
          <Box key={i} className={classes.timelineItem}>
            <Text span className={classes.timelineAction}>{t(`returns:timeline.${e.action}`, { defaultValue: e.action })}</Text>
            <Text span className={classes.timelineMeta}>
              {dateTimeShortFormat(e.timestamp)}
              {e.actorName ? ` · ${e.actorName}` : ""}
            </Text>
            {e.notes && <Text span className={classes.timelineMeta}>{e.notes}</Text>}
          </Box>
        ))}
      </Box>
    </Box>
  ) : null;

  const wrapperClass = `${classes.root} ${mode === "page" ? classes.page : ""}`;

  // Нийлүүлэгчийн шийдвэрлэх маягт (REQUESTED / DISPUTED).
  if (canDecide) {
    return (
      <Box className={wrapperClass}>
        {head}
        <ReturnProposalForm
          key={rr.version}
          returnRequest={rr}
          isRepropose={disputed}
          onReject={() => setRejectOpen(true)}
          onSent={refresh}
          lead={<>{requestCard}{disputeBlock}</>}
        />
        <RejectModal opened={rejectOpen} onClose={() => setRejectOpen(false)} returnRequestId={rr.id} onSuccess={refresh} />
        <Modal opened={!!zoom} onClose={() => setZoom(null)} size="lg" withCloseButton={false} centered padding={0}>
          {zoom && <Image src={zoom} alt="" />}
        </Modal>
      </Box>
    );
  }

  const buyerCanRespond = !isSeller && rr.status === "PROPOSED";
  const cancellable = !isSeller && CANCELLABLE_STATUSES.includes(rr.status);
  const needsOrderModule = waitingOn === "ME" && isSeller && action && action !== "decide" && action !== "repropose";

  return (
    <Box className={wrapperClass}>
      {head}
      <Box className={classes.body}>
        {requestCard}
        {disputeBlock}
        {waitingOn === "THEM" && <Text className={classes.hintText}>{t("returns:decision.waitingThem")}</Text>}
        {stage === "DONE" && <Text className={classes.hintText}>{t("returns:decision.doneNote")}</Text>}
        {stage === "CLOSED" && <Text className={classes.hintText}>{t("returns:decision.closedNote")}</Text>}
        {rr.status === "REJECTED" && kv(t("returns:decision.rejection"), rr.rejectionReason)}
        {proposalSummary}
        {kv(t("returns:decision.creditNote"), rr.creditNoteNumber)}
        {needsOrderModule && (
          <Box className={classes.stepInfo}>
            <Text span>{t(`returns:decision.stepHint.${action}`, { defaultValue: t("returns:decision.stepHint.open") })}</Text>
            <Button component="a" href={orderModuleUrl} color="brand" w="fit-content">{t("returns:decision.openInOrder")}</Button>
          </Box>
        )}
        {historyBlock}
      </Box>
      {(buyerCanRespond || cancellable) && (
        <Box className={classes.foot}>
          <Box>
            {cancellable && <Button variant="subtle" color="gray" onClick={handleCancel}>{t("returns:decision.cancel")}</Button>}
          </Box>
          {buyerCanRespond && (
            <Box className={classes.footRight}>
              <Button variant="default" onClick={() => setDisputeOpen(true)}>{t("returns:decision.disputeBtn")}</Button>
              <Button color="brand" onClick={handleAccept}>{t("returns:decision.accept")}</Button>
            </Box>
          )}
        </Box>
      )}
      <DisputeModal
        opened={disputeOpen}
        onClose={() => setDisputeOpen(false)}
        returnRequest={{ id: rr.id, disputeCount: rr.disputeCount || 0, returnNumber: rr.returnNumber || "", version: rr.version }}
        onSuccess={refresh}
      />
      <Modal opened={!!zoom} onClose={() => setZoom(null)} size="lg" withCloseButton={false} centered padding={0}>
        {zoom && <Image src={zoom} alt="" />}
      </Modal>
    </Box>
  );
}

type DrawerProps = {
  returnId: string | null;
  onClose: () => void;
  onChanged?: () => void;
};

export function ReturnDecisionDrawer({ returnId, onClose, onChanged }: DrawerProps) {
  const { t } = useTranslation("returns");
  return (
    <Drawer
      opened={!!returnId}
      onClose={onClose}
      position="right"
      size={600}
      withCloseButton={false}
      padding={0}
      title={null}
      aria-label={t("decision.title")}
      styles={{ content: { display: "flex", flexDirection: "column" }, body: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column", padding: 0 } }}
    >
      {returnId && <ReturnDecisionView key={returnId} id={returnId} mode="drawer" onClose={onClose} onChanged={onChanged} />}
    </Drawer>
  );
}
